import * as React from 'react';
import { useContext, useState, ReactNode } from 'react';
import { Snackbar, SnackbarOrigin } from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';

interface SnackbarContextType {
    showSnackbar: (message: string, severity?: AlertProps['severity'], duration?: number) => void;
}

const SnackbarContext = React.createContext<SnackbarContextType | undefined>(undefined);

export const useSnackbar = () => {
    const context = useContext(SnackbarContext);
    if (!context) {
        throw new Error('useSnackbar must be used within a SnackbarProvider');
    }
    return context;
};

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface SnackbarProviderProps {
    children: ReactNode;
}

export const SnackbarProvider: React.FC<SnackbarProviderProps> = ({ children }) => {
    const [snackbar, setSnackbar] = useState<{ message: string; severity?: AlertProps['severity']; open: boolean; duration?: number }>({
        message: '',
        severity: 'info',
        open: false,
        duration: 3000,
    });

    const showSnackbar = (message: string, severity: AlertProps['severity'] = 'info', duration: number = 3000) => {
        setSnackbar({ message, severity, open: true, duration });
    };

    const handleClose = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <SnackbarContext.Provider value={{ showSnackbar }}>
            {children}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={snackbar.duration}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' } as SnackbarOrigin}
            >
                <Alert onClose={handleClose} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </SnackbarContext.Provider>
    );
};
